import useLinkProps from '@/components/Link/hooks/useLinkProps';
import { graphql } from 'gatsby';
import { forwardRef } from 'react';
import {
  StyledLink,
  StyledTitle,
  StyledIcon,
  StyledDescription,
  StyledImage,
} from './NavigationDropdownLink.styles';
import InfoTag from '@/components/InfoTag/InfoTag';

const DEFAULT_IMAGE_WIDTH = 176;
const DEFAULT_IMAGE_HEIGHT = 48;

interface NavigationDropdownLinkProps extends Queries.NAVIGATION_DROPDOWN_LINKFragment {
  tabIndex?: number;
  onTouchStart?: (event: React.TouchEvent) => void;
  onTouchEnd?: (event: React.TouchEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

const NavigationDropdownLink = forwardRef<HTMLAnchorElement, NavigationDropdownLinkProps>(
  ({ elements, tabIndex, onTouchStart, onTouchEnd, onKeyDown }, ref) => {
    const {
      image,
      link: {
        value: { linkElements },
      },
      description,
      info_tag,
    } = elements;
    const { linkChildren, linkProps } = useLinkProps({
      action: linkElements.action.value,
      target: linkElements.target?.value,
      event: linkElements.event?.value,
    });
    const imageValue = image?.value && {
      ...image.value,
      description: image.value.description || undefined,
      width: image.value.width ?? DEFAULT_IMAGE_WIDTH,
      height: image.value.height ?? DEFAULT_IMAGE_HEIGHT,
    };
    const hasIcon = !!linkElements.icon?.value?.url;
    const hasInfoTag = !!info_tag?.value?.system.id;

    // Generate unique IDs for ARIA relationships
    const linkId = `dropdown-link-${elements.link.value.system.id}`;
    const descriptionId = description?.value ? `${linkId}-description` : undefined;

    return (
      <li>
        <StyledLink
          {...linkProps}
          id={linkId}
          ref={ref}
          tabIndex={tabIndex}
          aria-describedby={descriptionId}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onKeyDown={onKeyDown}
        >
          {imageValue && (
            <StyledImage
              image={{
                ...imageValue,
                image_id: imageValue.url,
              }}
              width={imageValue.width}
              height={imageValue.height}
              alt={imageValue.description || ''}
              layout="fullWidth"
              objectFit="contain"
            />
          )}
          {linkChildren}
          <StyledTitle>
            {hasIcon && <StyledIcon {...linkElements.icon.value} />}
            {linkElements.text.value}
            {hasInfoTag && <InfoTag {...info_tag.value} size="small" />}
          </StyledTitle>
          {description?.value && (
            <StyledDescription id={descriptionId}>{description.value}</StyledDescription>
          )}
        </StyledLink>
      </li>
    );
  }
);

export const fragment = graphql`
  fragment NAVIGATION_DROPDOWN_LINK on kontent_item_navigation_dropdown_link {
    system {
      id
    }
    elements {
      image {
        value {
          description
          url
          width
          height
        }
      }
      link {
        value {
          system {
            id
          }
          ...LINK
        }
      }
      description {
        value
      }
      info_tag {
        value {
          system {
            id
          }
          ...INFO_TAG
        }
      }
    }
  }
`;

export default NavigationDropdownLink;
